import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  Badge,
  Avatar,
  InputBase,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  Theme,
  Popover,
  Fade,
  Grow,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  LogOut,
  Settings,
  User,
  ChevronDown,
  Menu as MenuIcon,
  Search,
  X,
  ListFilter,
} from "lucide-react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useAppSelector } from "../../redux/typedHooks";

import { UserInterface } from "../../types/user";
import Logout from "../../utils/logout";
import {
  getFileByIdAPI,
  markAllInAppNotificationsAsReadAPI,
} from "../../api/userApi";
import InAppNotifications from "./InAppNotifications/InAppNotifications";
import Logo from "../../assets/Logo.svg";
import defaultAvatar from "../../assets/images/icons/Frame 1000005792.png";
import { useHeaderStyles } from "./style";
import UserStatusWidget from "./UserStatusWidget";
import BellIconWhite from "../../assets/images/belliconwhite";
import premiumLogo from "../../assets/premium 1.png";
import { searchAll } from "../../api/userApi";
import { getCategoriesAPI } from "../../api/categoriesApi";
import useMonetization from "../../hooks/useMonetization";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../query/keys";
import { useInAppNotificationsUnreadCountQuery } from "../../query/hooks/useInAppNotificationsUnreadCountQuery";
import { showToast } from "../../utils/toast";
import {
  AdvancedCreatorFilters,
  ADVANCED_FILTER_KEYS,
  defaultAdvancedCreatorFilters,
  parseAdvancedFiltersFromSearch,
  serializeAdvancedFilters,
} from "../Main/Home/SuggestionsCreators/filtering";

type HeaderProps = {
  onMenuClick?: () => void;
};

interface ISearchedUser {
  _id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  profileImg?: string;
}

interface ISearchedPost {
  _id?: string;
  id?: string;
  description?: string;
  user?: {
    username?: string;
    firstName?: string;
    lastName?: string;
  };
}

const HeaderSearchBar: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const history = useHistory();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [users, setUsers] = useState<ISearchedUser[]>([]);
  const [posts, setPosts] = useState<ISearchedPost[]>([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [userAvatars, setUserAvatars] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<
    Array<{ _id: string; name: string }>
  >([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] =
    useState<AdvancedCreatorFilters>(defaultAdvancedCreatorFilters);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const { subscription } = useMonetization();
  const premiumTiers = ["onyx", "sapphire", "diamond", "platinum_vip"];
  const normalizedTier = subscription?.tier?.toLowerCase?.();
  const normalizedStatus = subscription?.status?.toLowerCase?.();
  const canUsePremiumFilters = Boolean(
    subscription &&
    normalizedStatus === "active" &&
    normalizedTier &&
    premiumTiers.includes(normalizedTier)
  );
  const serializedUrlAdvancedFilters = useMemo(
    () =>
      serializeAdvancedFilters(parseAdvancedFiltersFromSearch(location.search)),
    [location.search]
  );

  useEffect(() => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setTags([]);
      setUsers([]);
      setPosts([]);
      setOpenDropdown(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => {
      const useCreatorsDiscoveryMode =
        advancedFiltersOpen ||
        Object.keys(serializedUrlAdvancedFilters).length > 0;

      if (useCreatorsDiscoveryMode) {
        const query = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
        searchAll(query, {
          scope: "creators",
          page: 1,
          perPage: 20,
          ...serializedUrlAdvancedFilters,
        })
          .then((res: any) => {
            const creatorItems = (res.items || []) as ISearchedUser[];
            setUsers(creatorItems);
            setPosts([]);
            setTags([]);
            setOpenDropdown(creatorItems.length > 0);
          })
          .catch(() => {
            setUsers([]);
            setPosts([]);
            setTags([]);
            setOpenDropdown(false);
          });
        return;
      }

      // Пошук по хештегу (#tag) – показуємо популярні теги
      if (trimmed.startsWith("#")) {
        const query = trimmed.slice(1);
        if (!query) {
          setTags([]);
          setUsers([]);
          setPosts([]);
          setOpenDropdown(false);
          return;
        }
        searchAll(query)
          .then(res => {
            const tagSet = new Set<string>();
            (res.hashtags || []).forEach((item: any) => {
              (item.hashtags || []).forEach((tag: string) => {
                if (tag.toLowerCase().startsWith(query.toLowerCase())) {
                  tagSet.add(tag.toLowerCase());
                }
              });
            });
            const list = Array.from(tagSet).slice(0, 5);
            setTags(list);
            const foundUsers = (res.users || []) as ISearchedUser[];
            const foundPosts = (res.posts || []) as ISearchedPost[];
            setUsers(foundUsers);
            setPosts(foundPosts);
            setOpenDropdown(
              list.length > 0 || foundUsers.length > 0 || foundPosts.length > 0
            );
          })
          .catch(() => {
            setTags([]);
            setUsers([]);
            setPosts([]);
            setOpenDropdown(false);
          });
        return;
      }

      // Звичайний текстовий пошук – показуємо результати користувачів/постів
      searchAll(trimmed)
        .then(res => {
          const foundUsers = (res.users || []) as ISearchedUser[];
          const foundPosts = (res.posts || []) as ISearchedPost[];
          setUsers(foundUsers);
          setPosts(foundPosts);
          setTags([]);
          setOpenDropdown(foundUsers.length > 0 || foundPosts.length > 0);
        })
        .catch(() => {
          setUsers([]);
          setPosts([]);
          setOpenDropdown(false);
        });
    }, 250);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [searchValue, advancedFiltersOpen, serializedUrlAdvancedFilters]);

  // Load avatar blobs for search users
  useEffect(() => {
    const loadAvatars = async () => {
      for (const user of users) {
        if (!user.profileImg) continue;
        if (userAvatars[user._id]) continue;

        try {
          const src = user.profileImg.includes("/api/files/")
            ? await getFileByIdAPI(user.profileImg.split("/api/files/")[1])
            : user.profileImg;

          if (typeof src === "string") {
            setUserAvatars(prev => ({
              ...prev,
              [user._id]: src,
            }));
          }
        } catch {
          // fall back to default avatar silently
        }
      }
    };

    if (users.length) {
      loadAvatars();
    }
  }, [users, userAvatars]);

  useEffect(() => {
    let cancelled = false;
    getCategoriesAPI()
      .then(
        (res: {
          success?: boolean;
          data?: Array<{ _id: string; name: string }>;
        }) => {
          if (!cancelled && res?.success && res.data) {
            setCategories(res.data);
          }
        }
      )
      .catch(() => {
        /* categories optional for search bar */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setAdvancedFilters(parseAdvancedFiltersFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    if (!openDropdown) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const root = searchBarRef.current;
      if (!root) return;
      const target = event.target as Node | null;
      if (target && !root.contains(target)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [openDropdown]);

  const handleTagClick = (tag: string) => {
    const sp = new URLSearchParams(location.search);
    sp.set("visionType", "Explore");
    sp.set("tag", tag);
    history.push({ pathname: "/", search: sp.toString() });
    setOpenDropdown(false);
  };

  const handlePostClick = (postId: string | undefined) => {
    if (!postId) return;
    const sp = new URLSearchParams(location.search);
    sp.set("visionType", "Explore");
    sp.set("postId", postId);
    history.push({ pathname: "/", search: sp.toString() });
    setOpenDropdown(false);
    setSearchValue("");
    onClose?.();
  };

  const handleUserClick = (username: string) => {
    history.push(`/${username}`);
    setSearchValue("");
    setOpenDropdown(false);
    onClose?.();
  };

  const handleTagClickWithClose = (tag: string) => {
    handleTagClick(tag);
    onClose?.();
  };

  const searchParams = new URLSearchParams(location.search);
  const selectedCategoryIdFromUrl = searchParams.get("categoryId");

  const applyExploreCategory = (categoryId: string | null) => {
    const sp = new URLSearchParams(location.search);
    sp.set("visionType", "Explore");
    if (categoryId) {
      sp.set("categoryId", categoryId);
    } else {
      sp.delete("categoryId");
    }
    history.push({ pathname: "/", search: sp.toString() });
    setFilterAnchorEl(null);
    onClose?.();
  };

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const filterMenuOpen = Boolean(filterAnchorEl);

  const textFieldBaseProps = {
    InputLabelProps: { style: { color: "#FFFFFFB2" } },
    InputProps: { style: { color: "#FFFFFF" } },
    FormHelperTextProps: { style: { color: "#FFFFFF80" } },
  } as const;

  const selectTextFieldProps = {
    ...textFieldBaseProps,
    SelectProps: {
      MenuProps: {
        PaperProps: {
          style: {
            background: "#1b1b1b",
            color: "#fff",
          },
        },
      },
      style: { color: "#FFFFFF" },
    },
  } as const;

  const handleAdvancedFilterChange = (
    field: keyof AdvancedCreatorFilters,
    value: string
  ) => {
    setAdvancedFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyAdvancedFiltersToExplore = () => {
    const serialized = serializeAdvancedFilters(advancedFilters);
    const sp = new URLSearchParams(location.search);
    sp.set("visionType", "Explore");

    ADVANCED_FILTER_KEYS.forEach(key => sp.delete(key));

    Object.entries(serialized).forEach(([key, value]) => {
      sp.set(key, String(value));
    });

    history.push({ pathname: "/", search: sp.toString() });
    setAdvancedFiltersOpen(false);
    onClose?.();
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters(defaultAdvancedCreatorFilters);
  };

  return (
    <div
      ref={searchBarRef}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        backgroundColor: "#FFFFFF0D",
        borderRadius: 32,
        padding: "6px 6px 6px 12px",
        minWidth: isMobile ? "100%" : 340,
        maxWidth: isMobile ? "100%" : 480,
        width: isMobile ? "100%" : "auto",
        border: "1px solid #FFFFFF0D",
      }}
    >
      <Search
        size={18}
        color="#FFFFFF"
        style={{ marginRight: 8, flexShrink: 0 }}
      />
      <InputBase
        placeholder="Search"
        value={searchValue}
        onChange={e => setSearchValue(e.target.value)}
        inputProps={{ "aria-label": "search" }}
        style={{
          color: "#FFFFFF",
          flex: 1,
          fontSize: 14,
          minWidth: 0,
        }}
      />

      <Box
        style={{
          width: 1,
          height: 22,
          backgroundColor: "#FFFFFF26",
          margin: "0 4px 0 8px",
          flexShrink: 0,
        }}
      />

      <IconButton
        size="small"
        aria-label="Filter by category"
        aria-haspopup="true"
        aria-expanded={filterMenuOpen}
        onClick={e => {
          e.stopPropagation();
          setFilterAnchorEl(prev =>
            prev ? null : (e.currentTarget as HTMLElement)
          );
        }}
        style={{
          color: "#FFFFFF",
          padding: 6,
          flexShrink: 0,
        }}
      >
        <ListFilter
          size={20}
          strokeWidth={2}
          color={selectedCategoryIdFromUrl ? "#FE5A97" : "#FFFFFF"}
        />
      </IconButton>

      <Popover
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ zIndex: 1600 }}
        PaperProps={{
          style: {
            marginTop: 8,
            backgroundColor: "#1E1E1E",
            borderRadius: 12,
            border: "1px solid #FFFFFF14",
            boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
            minWidth: isMobile ? 260 : 280,
            maxWidth: 320,
            maxHeight: isMobile ? "50vh" : 360,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box style={{ padding: "12px 14px 8px" }}>
          <Typography
            variant="subtitle2"
            style={{ color: "#FFFFFF99", fontWeight: 600, fontSize: 11 }}
          >
            Categories
          </Typography>
          {/* <Box
            onClick={() => {
              setFilterAnchorEl(null);
              setAdvancedFiltersOpen(true);
            }}
            style={{
              marginTop: 10,
              background: "#FE5A971A",
              border: "1px solid #FE5A9755",
              color: "#fff",
              padding: "8px 10px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Advanced filters (Premium)
          </Box> */}
        </Box>
        <Box
          style={{
            overflowY: "auto",
            padding: "0 8px 10px",
          }}
        >
          <Box
            onClick={() => applyExploreCategory(null)}
            style={{
              padding: "10px 10px",
              borderRadius: 8,
              cursor: "pointer",
              color: "#FFFFFF",
              fontSize: 14,
              backgroundColor: !selectedCategoryIdFromUrl
                ? "#FFFFFF12"
                : "transparent",
            }}
          >
            All categories
          </Box>
          {categories.map(cat => {
            const active = selectedCategoryIdFromUrl === cat._id;
            return (
              <Box
                key={cat._id}
                onClick={() => applyExploreCategory(cat._id)}
                style={{
                  padding: "10px 10px",
                  borderRadius: 8,
                  cursor: "pointer",
                  color: "#FFFFFF",
                  fontSize: 14,
                  backgroundColor: active ? "#FFFFFF12" : "transparent",
                }}
              >
                {cat.name}
              </Box>
            );
          })}
        </Box>
      </Popover>

      {openDropdown &&
        (tags.length > 0 || users.length > 0 || posts.length > 0) && (
          <Box
            style={{
              position: "absolute",
              top: "110%",
              left: 0,
              right: 0,
              backgroundColor: "#1E1E1E",
              borderRadius: 12,
              padding: "6px 0",
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              zIndex: 1300,
              maxHeight: isMobile ? "60vh" : "400px",
              overflowY: "auto",
            }}
          >
            {/* Popular hashtags when searching by #tag */}
            {tags.map(tag => (
              <Box
                key={tag}
                onClick={() => handleTagClickWithClose(tag)}
                style={{
                  padding: "6px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#FFFFFF",
                  fontSize: 13,
                }}
              >
                <span>#{tag}</span>
                <Typography
                  variant="caption"
                  style={{ color: "#FFFFFF80", fontSize: 11 }}
                >
                  Popular
                </Typography>
              </Box>
            ))}

            {/* User search results */}
            {users.map(user => (
              <Box
                key={user._id}
                onClick={() => handleUserClick(user.username)}
                style={{
                  padding: "6px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  color: "#FFFFFF",
                  fontSize: 13,
                  borderTop: tags.length ? "1px solid #333" : "none",
                }}
              >
                <Avatar
                  src={userAvatars[user._id] || defaultAvatar}
                  style={{
                    width: 32,
                    height: 32,
                    marginRight: 8,
                  }}
                />
                <Box display="flex" flexDirection="column">
                  <span>
                    {user.firstName || user.lastName
                      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                      : `@${user.username}`}
                  </span>
                  <Typography
                    variant="caption"
                    style={{ color: "#FFFFFF80", marginTop: 2 }}
                  >
                    @{user.username}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Post search results when typing regular query */}
            {posts.map(post => {
              const id = post._id || post.id;
              if (!id) return null;

              const title =
                post.description && post.description.length > 80
                  ? `${post.description.slice(0, 80)}…`
                  : post.description || "Post";

              const username =
                post.user?.username ||
                [post.user?.firstName, post.user?.lastName]
                  .filter(Boolean)
                  .join(" ");

              return (
                <Box
                  key={id}
                  onClick={() => handlePostClick(id)}
                  style={{
                    padding: "6px 12px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    color: "#FFFFFF",
                    fontSize: 13,
                    borderTop: tags.length ? "1px solid #333" : "none",
                  }}
                >
                  <span>{title}</span>
                  {username && (
                    <Typography
                      variant="caption"
                      style={{ color: "#FFFFFF80", marginTop: 2 }}
                    >
                      @{username}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

      <Dialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            background: "#121212",
            color: "#fff",
            borderRadius: 14,
            border: "1px solid #2e2e2e",
          },
        }}
      >
        <DialogTitle style={{ borderBottom: "1px solid #2a2a2a" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Advanced Search Filters</Typography>
            <IconButton
              size="small"
              style={{ color: "#fff" }}
              onClick={() => setAdvancedFiltersOpen(false)}
            >
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          {!canUsePremiumFilters && (
            <Box
              style={{
                border: "1px solid #47343d",
                borderRadius: 10,
                background: "#2a1b23",
                padding: 10,
                marginBottom: 12,
              }}
            >
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                Unlock advanced creator filters with Premium.
              </Typography>
              <Button
                variant="contained"
                style={{
                  background: "#FE5A97",
                  color: "#fff",
                  textTransform: "none",
                }}
                onClick={() => {
                  setAdvancedFiltersOpen(false);
                  history.push("/subscriptions");
                }}
              >
                Upgrade to Premium
              </Button>
            </Box>
          )}

          <Box
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 10,
              opacity: canUsePremiumFilters ? 1 : 0.8,
            }}
          >
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Location"
              value={advancedFilters.location}
              onChange={e =>
                handleAdvancedFilterChange("location", e.target.value)
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Ethnicity"
              value={advancedFilters.ethnicity}
              onChange={e =>
                handleAdvancedFilterChange("ethnicity", e.target.value)
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Age min"
              type="number"
              value={advancedFilters.ageMin}
              onChange={e =>
                handleAdvancedFilterChange("ageMin", e.target.value)
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Age max"
              type="number"
              value={advancedFilters.ageMax}
              onChange={e =>
                handleAdvancedFilterChange("ageMax", e.target.value)
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Body type"
              value={advancedFilters.bodyType}
              onChange={e =>
                handleAdvancedFilterChange("bodyType", e.target.value)
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Orientation"
              value={advancedFilters.orientation}
              onChange={e =>
                handleAdvancedFilterChange("orientation", e.target.value)
              }
            />
            <TextField
              {...selectTextFieldProps}
              select
              variant="outlined"
              size="small"
              label="Online"
              value={advancedFilters.onlineStatus}
              onChange={e =>
                handleAdvancedFilterChange("onlineStatus", e.target.value)
              }
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
            </TextField>
            <TextField
              {...selectTextFieldProps}
              select
              variant="outlined"
              size="small"
              label="Verification"
              value={advancedFilters.verification}
              onChange={e =>
                handleAdvancedFilterChange("verification", e.target.value)
              }
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="unverified">Unverified</MenuItem>
            </TextField>
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Subscription price min"
              type="number"
              value={advancedFilters.subscriptionPriceMin}
              onChange={e =>
                handleAdvancedFilterChange(
                  "subscriptionPriceMin",
                  e.target.value
                )
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Subscription price max"
              type="number"
              value={advancedFilters.subscriptionPriceMax}
              onChange={e =>
                handleAdvancedFilterChange(
                  "subscriptionPriceMax",
                  e.target.value
                )
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="PPV min"
              type="number"
              value={advancedFilters.ppvPriceMin}
              onChange={e =>
                handleAdvancedFilterChange("ppvPriceMin", e.target.value)
              }
            />
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="PPV max"
              type="number"
              value={advancedFilters.ppvPriceMax}
              onChange={e =>
                handleAdvancedFilterChange("ppvPriceMax", e.target.value)
              }
            />
            <TextField
              {...selectTextFieldProps}
              select
              variant="outlined"
              size="small"
              label="Creator status"
              value={advancedFilters.creatorStatus}
              onChange={e =>
                handleAdvancedFilterChange("creatorStatus", e.target.value)
              }
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="trending">Trending</MenuItem>
              <MenuItem value="top">Top</MenuItem>
            </TextField>
            <TextField
              {...textFieldBaseProps}
              variant="outlined"
              size="small"
              label="Categories"
              value={advancedFilters.categories}
              onChange={e =>
                handleAdvancedFilterChange("categories", e.target.value)
              }
              helperText="Comma separated"
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            style={{ marginTop: 14 }}
          >
            <Button
              onClick={resetAdvancedFilters}
              style={{ color: "#FE5A97", textTransform: "none" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              style={{
                background: "#FE5A97",
                color: "#fff",
                textTransform: "none",
              }}
              onClick={() => {
                if (!canUsePremiumFilters) {
                  showToast(
                    "Advanced filters are available for premium members",
                    "info"
                  );
                  return;
                }
                applyAdvancedFiltersToExplore();
              }}
            >
              Apply filters
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const history = useHistory();
  const classes = useHeaderStyles();
  const theme = useTheme();
  const [isLogout, setIsLogout] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
    defaultMatches:
      typeof window !== "undefined"
        ? window.innerWidth < theme.breakpoints.values.md
        : false,
  });
  const isSmallScreen = useMediaQuery("(max-width: 1320px)");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // Відкриття поповера сповіщень
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [refreshNotificationsSignal, setRefreshNotificationsSignal] =
    useState(0);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const currentUser = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const currentUsername = currentUser?.username;
  const userType = currentUser?.userType?.title;

  // Get subscription info with loading state
  const { subscription, loading: monetizationLoading } = useMonetization();

  const queryClient = useQueryClient();
  const unreadCountQuery = useInAppNotificationsUnreadCountQuery();
  const hasUnreadNotifications = Boolean(unreadCountQuery.data?.hasUnread);

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const res = await markAllInAppNotificationsAsReadAPI();
      return res;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.inAppNotificationsUnreadCount,
      });
      setRefreshNotificationsSignal(prev => prev + 1);
    },
  });

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (typeof window !== "undefined") {
      setViewportWidth(window.innerWidth);
    }
    if (!markAllReadMutation.isPending) {
      markAllReadMutation.mutate();
    }
  };

  const handleNotificationsClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => setIsLogout(true);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setProfileMenuAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchorEl(null);

  const profilePath = currentUsername
    ? userType === "creator"
      ? `/${currentUsername}`
      : `/${currentUsername}`
    : "/login";

  const [avatarSrc, setAvatarSrc] = useState<string>(defaultAvatar);
  const avatarUrlRef = useRef<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState<boolean>(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState<boolean>(false);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const profileImg = currentUser?.profileImg;

    const revokeBlobUrl = () => {
      if (avatarUrlRef.current && avatarUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(avatarUrlRef.current);
        avatarUrlRef.current = null;
      }
    };

    let isMounted = true;

    const loadAvatar = async () => {
      revokeBlobUrl();

      setAvatarLoading(true);

      if (!profileImg || profileImg === "default") {
        if (isMounted) {
          setAvatarSrc(defaultAvatar);
          setAvatarLoading(false);
        }
        return;
      }

      // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
      if (profileImg.includes("lh3.googleusercontent.com")) {
        if (isMounted) {
          setAvatarSrc(profileImg);
          setAvatarLoading(false);
        }
        return;
      }

      // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
      if (
        profileImg.startsWith("https://") ||
        profileImg.startsWith("http://")
      ) {
        if (isMounted) {
          setAvatarSrc(profileImg);
          setAvatarLoading(false);
        }
        return;
      }

      try {
        const fileId = profileImg.includes("/api/files/")
          ? profileImg.split("/api/files/")[1]
          : profileImg;
        const blobUrl = await getFileByIdAPI(fileId);
        if (typeof blobUrl === "string" && isMounted) {
          avatarUrlRef.current = blobUrl;

          setAvatarSrc(blobUrl);
        } else if (isMounted) {
          setAvatarSrc(defaultAvatar);
        }
      } catch (error) {
        console.error("Failed to load profile image:", error);
        if (isMounted) {
          setAvatarSrc(defaultAvatar);
        }
      } finally {
        if (isMounted) {
          setAvatarLoading(false);
        }
      }
    };

    loadAvatar();

    return () => {
      isMounted = false;
      revokeBlobUrl();
    };
  }, [currentUser?.profileImg]);

  const profileInitial =
    (
      currentUser?.firstName?.[0] ||
      currentUser?.username?.[0] ||
      currentUser?.email?.[0] ||
      "?"
    )?.toUpperCase() ?? "?";

  const open = Boolean(anchorEl);
  const profileMenuOpen = Boolean(profileMenuAnchorEl);
  const id = open ? "notifications-popover" : undefined;

  const notificationPanelWidth = 400;
  /** Панель прилягає до правого краю вікна (right: 0) */
  const desktopNotificationAnchorLeft = Math.max(
    0,
    viewportWidth - notificationPanelWidth
  );

  // Check subscription from monetization hook (more reliable)
  const hasActiveSubscription =
    subscription?.status && subscription.status.toLowerCase() === "active";

  // Show banner only after loading and if user has Diamond plan
  const showPremiumBanner =
    !!currentUser && !monetizationLoading && !hasActiveSubscription;

  return (
    <div className={classes.headerContainer}>
      {isLogout && <Logout />}
      <AppBar position="static" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Box
            style={{
              cursor: "pointer",
              marginRight: isSmallScreen ? 48 : 0,
            }}
            className={classes.logoContainer}
            onClick={() => history.push("/")}
          >
            <img src={Logo} alt="logo" />
          </Box>

          <Box
            style={{
              maxWidth: "1115px",
              width: "100%",
              display: "flex",
              alignItems: "center",
              margin: "0 auto",
            }}
          >
            {!isMobile && (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                }}
              >
                {showPremiumBanner && (
                  <Box
                    style={{
                      gap: 14,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <img src={premiumLogo} alt="premium-logo" />
                    <div>
                      <Typography
                        style={{
                          color: "#fff",
                          fontSize: "16px",
                          fontWeight: 600,
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        Get Free Premium Upgrade
                      </Typography>
                      <Typography
                        style={{
                          cursor: "pointer",
                          color: "#FE5A97",
                          fontWeight: 700,
                          textDecoration: "underline",
                        }}
                        onClick={() => history.push("/subscriptions")}
                      >
                        Try it Out!
                      </Typography>
                    </div>
                  </Box>
                )}

                <HeaderSearchBar />
              </Box>
            )}

            <Box className={classes.grow} />

            {currentUser && (
              <>
                {isMobile && (
                  <IconButton
                    onClick={() => setMobileSearchOpen(true)}
                    style={{
                      color: "#FFFFFF",
                      marginRight: 8,
                    }}
                    aria-label="Open search"
                  >
                    <Search size={20} />
                  </IconButton>
                )}
                <Box className={classes.subscriptionBadgeContainer}>
                  <UserStatusWidget />
                </Box>
              </>
            )}

            {currentUser ? (
              <>
                {!isMobile && (
                  <>
                    <Button
                      className={classes.profileButton}
                      onClick={handleProfileMenuOpen}
                      startIcon={
                        <Avatar
                          src={
                            !avatarLoading ? avatarSrc || undefined : undefined
                          }
                          className={classes.profileAvatar}
                          alt={currentUsername || "Profile"}
                          // onError={() => {
                          //   if (avatarSrc !== defaultAvatar) {
                          //     setAvatarSrc(defaultAvatar);
                          //   }
                          // }}
                        >
                          {avatarLoading ? (
                            <CircularProgress
                              size={isMobile ? 18 : 20}
                              style={{ color: "#ffffff" }}
                            />
                          ) : (
                            profileInitial
                          )}
                        </Avatar>
                      }
                      endIcon={
                        <ChevronDown size={16} style={{ color: "#fff" }} />
                      }
                    >
                      {isMobile ? currentUsername || "Profile" : "My Profile"}
                    </Button>
                    <Menu
                      anchorEl={profileMenuAnchorEl}
                      open={profileMenuOpen}
                      onClose={handleProfileMenuClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      getContentAnchorEl={null}
                      PaperProps={{ className: classes.profileMenuPaper }}
                    >
                      <MenuItem
                        style={{
                          padding: "4.3px 12px",
                          minHeight: "auto",
                        }}
                        component={Link}
                        to={profilePath}
                        onClick={handleProfileMenuClose}
                      >
                        <ListItemIcon>
                          <User size={18} color="#fff" />
                        </ListItemIcon>
                        <ListItemText primary="View Profile" />
                      </MenuItem>
                      <MenuItem
                        style={{
                          padding: "4.3px 12px",
                          minHeight: "auto",
                        }}
                        component={Link}
                        to="/settings"
                        onClick={handleProfileMenuClose}
                      >
                        <ListItemIcon>
                          <Settings size={18} color="#fff" />
                        </ListItemIcon>
                        <ListItemText primary="Settings" />
                      </MenuItem>
                      <MenuItem
                        style={{
                          padding: "4.3px 12px",
                          minHeight: "auto",
                          marginTop: 12,
                        }}
                        onClick={() => {
                          handleProfileMenuClose();
                          handleLogOut();
                        }}
                      >
                        <ListItemIcon>
                          <LogOut size={18} color="#fff" />
                        </ListItemIcon>
                        <ListItemText primary="Log out" />
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </>
            ) : (
              <Button
                component={Link}
                to="/login"
                className={classes.profileButton}
                startIcon={
                  <Avatar className={classes.profileAvatar} src={defaultAvatar}>
                    <User size={isMobile ? 16 : 20} />
                  </Avatar>
                }
                endIcon={<ChevronDown size={16} style={{ color: "#FF68D5" }} />}
              >
                {isMobile ? "Login" : "Sign In"}
              </Button>
            )}

            {!isMobile && (
              <IconButton
                className={classes.notificationButton}
                onClick={handleNotificationsOpen}
                aria-describedby={id}
              >
                <Badge
                  color="error"
                  variant="dot"
                  overlap="rectangular"
                  invisible={!hasUnreadNotifications}
                >
                  <BellIconWhite />
                </Badge>
              </IconButton>
            )}

            {isMobile && (
              <>
                <IconButton
                  className={classes.notificationButton}
                  onClick={handleNotificationsOpen}
                  aria-describedby={id}
                  style={{ marginRight: 8 }}
                >
                  <Badge
                    color="error"
                    variant="dot"
                    overlap="rectangular"
                    invisible={!hasUnreadNotifications}
                  >
                    <BellIconWhite />
                  </Badge>
                </IconButton>
                {onMenuClick && (
                  <IconButton
                    className={classes.notificationButton}
                    onClick={onMenuClick}
                    aria-label="Open navigation menu"
                  >
                    <MenuIcon size={24} />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Search Modal */}
      <Dialog
        open={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: "#0C0A09",
            margin: isMobile ? 0 : "auto",
            maxHeight: isMobile ? "100vh" : "90vh",
            height: isMobile ? "100vh" : "auto",
            borderRadius: isMobile ? 0 : 12,
          },
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #FFFFFF1A",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography
              variant="h6"
              style={{ color: "#FFFFFF", fontWeight: 600 }}
            >
              Search
            </Typography>
            <IconButton
              onClick={() => setMobileSearchOpen(false)}
              style={{ color: "#FFFFFF" }}
              aria-label="Close search"
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          style={{
            padding: "20px",
            paddingTop: "16px",
          }}
        >
          <HeaderSearchBar onClose={() => setMobileSearchOpen(false)} />
        </DialogContent>
      </Dialog>

      <Popover
        id={id}
        open={open}
        onClose={handleNotificationsClose}
        keepMounted
        TransitionComponent={isMobile ? Fade : Grow}
        TransitionProps={
          isMobile
            ? { timeout: { enter: 200, exit: 150 } }
            : { timeout: { enter: 225, exit: 195 } }
        }
        anchorReference="anchorPosition"
        anchorPosition={
          isMobile
            ? { top: 0, left: 0 }
            : { top: 0, left: desktopNotificationAnchorLeft }
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        BackdropProps={{
          invisible: false,
          style: { backgroundColor: "#000000B2" },
        }}
        PaperProps={{
          className: classes.notificationPopoverPaper,
          style: {
            maxWidth: "none",
            width: isMobile ? "100%" : notificationPanelWidth,
            marginTop: 0,
            right: isMobile ? "0px" : "auto",
            left: "auto",
            backgroundColor: "transparent",
            height: "100vh",
            maxHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: isMobile ? 0 : "12px 0 0 12px",
          },
        }}
        marginThreshold={0}
      >
        <InAppNotifications
          onNotificationRead={() => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.inAppNotificationsUnreadCount,
            });
          }}
          refreshSignal={refreshNotificationsSignal}
          onClose={handleNotificationsClose}
        />
      </Popover>
    </div>
  );
};

export default Header;
